import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '../../shared/LanguageSwitcher';
import { ButtonLink, PageHeader } from '../../shared/ui';

interface LegalSection {
  title: string;
  body: string[];
}

/**
 * The frame both legal pages share - a top bar (back home + language), the
 * reading column, the section list and the cross-link to the other document.
 *
 * The whole content is bilingual from the i18n files (NFR04: the front end
 * never ships prose outside i18n). `page` is only which of the two `legal.*`
 * namespaces to read, which is why the two cannot drift: the fr/en file pair
 * is the single source for both languages at once.
 *
 * `backToSignup` adds the primary exit for the flow that opened this page from
 * an unchecked consent box: a full-width button (Thumb Zone) that returns the
 * student to the form they were filling.
 */
export function LegalLayout({
  page,
  backToSignup = false,
}: {
  page: 'privacy' | 'terms';
  backToSignup?: boolean;
}) {
  const { t } = useTranslation();
  const base = `legal.${page}`;
  const other = page === 'privacy' ? 'terms' : 'privacy';
  const sections = t(`${base}.sections`, { returnObjects: true }) as LegalSection[];

  return (
    <div className="min-h-screen bg-surface pb-20">
      <header className="bg-surface-container-lowest border-b border-surface-container">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 min-h-[44px] font-title-md text-on-surface-variant hover:text-primary transition-colors duration-chip"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[22px]">
              arrow_back
            </span>
            {t('legal.backHome')}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-8">
        <PageHeader title={t(`${base}.title`)} subtitle={t(`${base}.intro`)} />

        {sections.map((section) => (
          <section key={section.title}>
            <h2 className="font-title-lg text-title-lg text-on-surface mt-8">{section.title}</h2>
            {section.body.map((paragraph) => (
              <p
                key={paragraph}
                className="font-body-md text-on-surface-variant mt-2 leading-relaxed"
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}

        <p className="font-label-sm text-on-surface-variant/60 mt-10">{t(`${base}.lastUpdated`)}</p>

        {backToSignup && (
          <ButtonLink to="/signup" className="w-full mt-8">
            <span aria-hidden="true" className="material-symbols-outlined text-[22px]">
              arrow_back
            </span>
            {t('legal.backToSignUp')}
          </ButtonLink>
        )}

        <nav className="mt-3 border-t border-surface-container pt-4">
          <Link
            to={`/${other}`}
            className="inline-flex items-center gap-2 min-h-[44px] font-title-md text-primary hover:text-primary-container transition-colors duration-chip"
          >
            {t(`legal.${other === 'privacy' ? 'privacyLink' : 'termsLink'}`)}
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              arrow_forward
            </span>
          </Link>
        </nav>
      </main>
    </div>
  );
}

export function PrivacyPage() {
  return <LegalLayout page="privacy" backToSignup />;
}

export function TermsPage() {
  return <LegalLayout page="terms" />;
}
